<template>
  <VueFinalModal
    v-model="isOpen"
    :class="modalClass"
    content-class="modal-content-wrapper"
    :overlay-transition="overlayTransition"
    :content-transition="contentTransition"
    :click-to-close="clickToClose"
    :esc-to-close="escToClose"
    @update:model-value="onUpdateModelValue"
    @opened="onOpened"
    @before-close="onBeforeClose"
    @closed="onClosed"
  >
    <div :class="contentClass" :style="contentStyle">
      <slot />
    </div>
  </VueFinalModal>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { VueFinalModal } from 'vue-final-modal';
import { modalManager } from '@/utils/useModal';

export default {
  name: 'BaseModal',
  components: {
    VueFinalModal
  },
  props: {
    name: {
      type: String,
      required: true
    },
    width: {
      type: [Number, String],
      default: 'auto'
    },
    height: {
      type: [Number, String],
      default: 'auto'
    },
    scrollable: {
      type: Boolean,
      default: false
    },
    adaptive: {
      type: Boolean,
      default: false
    },
    clickToClose: {
      type: Boolean,
      default: true
    },
    escToClose: {
      type: Boolean,
      default: true
    },
    overlayTransition: {
      type: String,
      default: 'vfm-fade'
    },
    contentTransition: {
      type: String,
      default: 'vfm-fade'
    }
  },
  emits: ['before-open', 'opened', 'before-close', 'closed'],
  setup(props, { emit }) {
    const isOpen = ref(false);

    const modalClass = computed(() => {
      return [
        'modal-wrapper',
        props.adaptive ? 'modal-adaptive' : ''
      ].filter(Boolean).join(' ');
    });

    const contentClass = computed(() => {
      return [
        'modal-container',
        props.scrollable ? 'modal-scrollable' : ''
      ].filter(Boolean).join(' ');
    });

    const contentStyle = computed(() => {
      const style = {};
      
      if (props.width && props.width !== 'auto') {
        const width = typeof props.width === 'number' ? `${props.width}px` : props.width;
        style.width = width;
        style.maxWidth = width;
      }
      
      if (props.height && props.height !== 'auto') {
        const height = typeof props.height === 'number' ? `${props.height}px` : props.height;
        style.height = height;
        style.maxHeight = height;
      }

      if (props.scrollable) {
        style.overflowY = 'auto';
      }
      
      return style;
    });

    const show = (params) => {
      emit('before-open', { params });
      isOpen.value = true;
    };

    const hide = () => {
      isOpen.value = false;
    };

    const onUpdateModelValue = (value) => {
      isOpen.value = value;
    };

    const onOpened = () => {
      emit('opened');
    };

    const onBeforeClose = () => {
      emit('before-close');
    };

    const onClosed = () => {
      emit('closed');
    };

    // Register with modal manager
    onMounted(() => {
      modalManager.register(props.name, { show, hide });
    });

    onUnmounted(() => {
      modalManager.unregister(props.name);
    });

    return {
      isOpen,
      modalClass,
      contentClass,
      contentStyle,
      onUpdateModelValue,
      onOpened,
      onBeforeClose,
      onClosed
    };
  }
};
</script>

<style>
/* Global styles for vue-final-modal - must not be scoped */
.modal-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
}

.modal-content-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: none;
}

.modal-content-wrapper > * {
  pointer-events: auto;
}

.modal-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  max-width: 90vw;
  max-height: 90vh;
  overflow: auto;
}

.modal-scrollable {
  overflow-y: auto;
}

.modal-adaptive .modal-container {
  width: auto !important;
  height: auto !important;
}
</style>
